import { Box, HStack, Image, VStack, Text } from '@chakra-ui/react';
import * as React from 'react';
import * as Images from '../Images';
import { Colors, Format } from 'core/ui';
import { SessionProvider } from 'core/infrastructure';
import { SvgIcon, CloseButton, BasicButton } from 'core/ui-next';
import { toShortUserName } from './UserButton';
import * as ThemeManager from './ThemeManager';
import { useHelp } from './ActionButton.HelpProvider';
import { useScreenSize } from './ScreenSize';
import { Res } from 'core/api';
import { Modal } from 'core/ui-next/modal';
import { Electron } from 'pos/common';
import { FabButton } from 'core/ui-next/FabButton';
import { ScreenSectionSymbols } from './PosScreen.Sections';
import { DateUtils } from 'core/utils';

export type HeaderProps = {
	title: string;
	image?: string | SvgIcon;
	onUserClick?: () => void;
	onClose: () => void;
	showKeyboard?: boolean;
	hideCloseButton?: boolean;

	//Estos props son para reutilizar la lógica de la cabecera en el wizard
	// Puede que sea mejor hacer unas cabeceras solo para el wizard?
	wizardHeader?: boolean;
	isCompositionVisible?: () => void;
}

const onKeyboardClick = async () => await Electron.Keyboard.toggle();

export function User(props: { onClick?: () => void }) {

	const onLogOut = React.useCallback(async () => {
		const result = await Modal.show(LogOutConfirmScreen, { userName: SessionProvider.getCurrent().UserName, closeOnInteractOutside: false });
		if (!result.ok)
			return;

		props.onClick?.();
	}, [props]);

	const isSessionStarted = SessionProvider.isSessionStarted();
	if (!isSessionStarted)
		return null;

	const userImageId = isSessionStarted ? SessionProvider.getCurrent().UserImageId : '';
	const userName = isSessionStarted ? SessionProvider.getCurrent().UserName : '';
	const userBackColor = isSessionStarted ? SessionProvider.getCurrent().UserBackColor : '';

	const hasImage = !(userImageId === Images.NULL_IMAGE || userImageId === '');
	const backgroundColor = Colors.getBackgroundColorAsRgb(userBackColor);

	return <HStack gap={'2'} alignItems={'center'} onClick={onLogOut} cursor={'pointer'}>
		<Text
			fontSize={'sm'}
			fontWeight={'semibold'}
			maxWidth={{base: '35', lgOnly: '35', xlOnly: '44', '2xl': '58'}}
			whiteSpace={'nowrap'}
			overflow={'hidden'}
			textOverflow={'ellipsis'}
		>
			{userName}
		</Text>
		{hasImage
			? <Image src={Images.imgSrcFromId(userImageId)} boxSize='10' borderRadius='full' fit='cover' alt={userName} />
			: <Box display={'flex'} alignItems={'center'} justifyContent={'center'} fontWeight={'bold'} fontSize={'lg'} h='10' w='10' rounded='full' bg={backgroundColor} color={Colors.getTextColor(backgroundColor)}><Text>{toShortUserName(userName)}</Text></Box>
		}
	</HStack>;
}

function LogOutConfirmScreen({ userName, close, cancel }: { userName: string } & Modal.Actions<void>) {

	return (
		<Box py={9} px={12} justifyItems={'center'} bg={{ base: 'white', _dark: '{colors.neutral.900}' }}>
			<SvgIcon src={Images.CloseSession} w={'12'} h={'12'} />
			<Box mt={6} mb={4.5}>
				{Res.Msg.MSG_CLOSE_USER_SESSION(userName)}
			</Box>
			<HStack gap={9}>
				<BasicButton w={36} type='secondary' text={Res.Common.CANCEL_BTN()} onClick={() => cancel()} />
				<BasicButton w={36} type='success' text={Res.Web.CLOSE_SESSION_ACTION_BTN()} onClick={() => close()} />
			</HStack>
		</Box>
	);
}

function Clock({ showDate }: { showDate: boolean }): React.ReactNode {

	const [now, setNow] = React.useState(new Date());

	const height = showDate ? '14' : '10';
	const paddingTop = showDate ? '2' : '0';

	React.useEffect(() => {
		const interval = setInterval(() => {
			setNow(new Date());
		}, 5000);

		return () => clearInterval(interval);
	}, []);

	return <VStack bg={'neutral.900'} borderRadius={'md'} justifyContent={'center'} height={height} gap={'0.5'} paddingX={'2'} paddingTop={paddingTop} >
		<Text fontSize={'xl'} fontWeight={'bold'} color={'white'}>{Format.asTime(now)}</Text>
		{showDate && <Text fontSize={'xs'} color={'neutral.300'}>{DateUtils.formatDate(now, 'd LLL, yyyy')}</Text>}
	</VStack>;
}

function FullScreenHeader({ title, onUserClick, showKeyboard }: { title: string, onUserClick?: () => void, showKeyboard?: boolean }): React.ReactNode {

	return <HStack w={'full'} h='14' bg={{ base: '{colors.blue.700}', _dark: '{colors.blue.900}' }} pt={'2'} pr={'2'} pb={'2'} pl={'4'} gap='4' color={{ base: 'white', _dark: 'white' }} boxShadow={'0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.10)'} zIndex={1}>
		<Box h='full' display={'flex'} alignItems={'center'}>
			<SvgIcon src={Images.AgoraLogo} h={'8'} w={'8'} />
		</Box>
		<Box flexGrow={1} h='full' display={'flex'} alignItems={'center'}>
			<Box fontSize={'md'} fontWeight={'semibold'} m={0}>{title}</Box>
		</Box>
		<HStack h='full' display={'flex'} justifyContent={'center'} alignItems={'center'} gap={'4'}>
			{Electron.isEnabled() && showKeyboard ? <FabButton src={Images.Keyboard} onClick={onKeyboardClick} /> : null}
			{/* Aquí ocultamos la info del usuario en pantallas completas donde no se envía la función de cierre de sesión
			ya que no tiene mucho sentido mostrar el usuario si no se puede cerrar sesión o interactuar con ello, asi
			creamos un camino claro para el usuario de cuando interactuar con eso o no
			*/}
			{onUserClick &&
				<User onClick={onUserClick} />
			}
			<Clock showDate={false} />
		</HStack>
	</HStack>;
}

function ModalHeader({ title, onClose, showKeyboard, hideCloseButton }: { title: string, onClose: () => void, showKeyboard?: boolean, hideCloseButton?: boolean }): React.ReactNode {

	const pos = SessionProvider.isSessionStarted() ? SessionProvider.getCurrent().PosName : '';
	const user = SessionProvider.isSessionStarted() ? SessionProvider.getCurrent().UserName : '';

	const loggedUser = user ? `${pos} - ${user}` : '';

	return <HStack w={'full'} h='12' pt={'2'} pr={'3'} pb={'2'} pl={'3'} bg={{ base: '{colors.blue.700}', _dark: '{colors.blue.900}' }} color={{ base: 'white', _dark: 'white' }}>
		<Box flexGrow={1} h='full' display={'flex'} alignItems={'center'} lineHeight={'120%'}>
			<VStack display={'block'}>
				<Box fontSize={'sm'} fontWeight={'semibold'} m={0}>{title}</Box>
				{loggedUser ? <Text width={'full'} fontSize={'xs'} fontWeight={'semibold'} truncate>{loggedUser}</Text> : null}
			</VStack>
		</Box>
		{Electron.isEnabled() && showKeyboard ? <FabButton src={Images.Keyboard} onClick={onKeyboardClick} /> : null}
		{hideCloseButton ? null : <CloseButton color={{ base: 'white', _dark: 'white' }} onClick={onClose} justifyContent={'end'} _hover={{ bg: 'none' }} />}
	</HStack>;
}

const HelpButton = () => {
	const { helpActive, toggleHelp } = useHelp();
	return <SvgIcon src={helpActive ? Images.HelpActive : Images.HelpInactive} onClick={toggleHelp} h='8' w='8' />;
};

function PhoneHeader({ title, image, wizardHeader, isCompositionVisible }: { title: string, image: string | SvgIcon, wizardHeader?: boolean, isCompositionVisible?: () => void }): React.ReactNode {
	const user = SessionProvider.isSessionStarted() ? SessionProvider.getCurrent().UserName : '';

	const loggedUser = user ? `| ${user}` : '';

	return <HStack w={'full'} h='12' bg={{ base: '{colors.blue.700}', _dark: '{colors.blue.900}' }} pt={'2'} pr={'2'} pb={'2'} pl={'4'} color={{ base: 'white', _dark: 'white' }}>
		<Box h='full' display={'flex'} justifyContent={'center'} alignItems={'center'}>
			{typeof image === 'string' ? <Image rounded="full" src={image} h={'7'} w={'7'} /> : <SvgIcon h='7' minW='7' src={image}></SvgIcon>}
		</Box>
		<Text width={'full'} fontSize={'sm'} fontWeight={'semibold'} truncate>
			{title}&nbsp;{loggedUser}
		</Text>
		{wizardHeader ?
			<VStack w={'8'} h={'8'} justifyContent={'center'} alignItems={'center'}>
				<SvgIcon src={Images.ViewTicket} onClick={isCompositionVisible} />
			</VStack>
			: null
		}
		<Box h='full' display={'flex'} justifyContent={'center'} alignItems={'center'}>
			<HelpButton />
		</Box>
	</HStack>;
}

export function Header({ title, image, onClose, onUserClick, showKeyboard = true, hideCloseButton, wizardHeader, isCompositionVisible }: HeaderProps): React.ReactNode {

	const size = useScreenSize();

	if (ThemeManager.isPhone()) {
		return <PhoneHeader title={title} image={image ?? Images.AgoraLogo} wizardHeader={wizardHeader} isCompositionVisible={isCompositionVisible} />;
	}

	return size === 'full-screen' ? <FullScreenHeader title={title} onUserClick={onUserClick} showKeyboard={showKeyboard} /> : <ModalHeader title={title} onClose={onClose} showKeyboard={showKeyboard} hideCloseButton={hideCloseButton} />;
}
(Header as any)[ScreenSectionSymbols.Header] = true;

export type SaleHeaderProps = {
	content: React.ReactNode;
	onUserClick?: () => void
	showKeyboard?: boolean;
}

export function SaleHeader({ content, onUserClick, showKeyboard = true }: SaleHeaderProps) {

	const rightContent = ThemeManager.isPhone()
		? <HelpButton />
		: <HStack gap={'2'}>
			{Electron.isEnabled() && showKeyboard
				? <FabButton src={Images.Keyboard} onClick={onKeyboardClick} />
				: null}
			<User onClick={onUserClick} />
			{ThemeManager.isLargeDevice() && <Clock showDate={true} />}
		</HStack>;

	return <HStack
		w={'full'}
		h={ThemeManager.isPhone() ? '14' : '18'}
		bg={{ base: '{colors.blue.700}', _dark: '{colors.blue.900}' }}
		pt={ThemeManager.isPhone() ? '2' : '2'}
		pr={ThemeManager.isPhone() ? '3' : '2'}
		pb={ThemeManager.isPhone() ? '2' : '2'}
		pl={ThemeManager.isPhone() ? '3' : '4'}
		gap={ThemeManager.isPhone() ? '2' : '4'}
		color={{ base: 'white', _dark: 'white' }}
	>
		<Box h='full' display={'flex'} justifyContent={'center'} alignItems={'center'}>
			<SvgIcon src={Images.AgoraLogo} h={ThemeManager.isPhone() ? '7' : '8'} w={ThemeManager.isPhone() ? '7' : '8'} />
		</Box>
		<Box flex={1} h='full' overflow={'hidden'}>
			{content}
		</Box>
		<Box h='full' display={'flex'} justifyContent={'center'} alignItems={'center'}>
			{rightContent}
		</Box>
	</HStack>;
}
(SaleHeader as any)[ScreenSectionSymbols.Header] = true;
